import { Component, createElement, Fragment, lazy } from 'react';
import type { ComponentType, PropsWithChildren, ReactNode } from 'react';

import { SystemBanner } from '../foundation/state';

/**
 * A lazily loaded screen whose loader can be rebuilt. React keeps a rejected `lazy` rejected, so a
 * retry needs a new `lazy(loader)`; `reset` builds one only when the last load failed, so a screen
 * that loaded keeps its component type (and its mounted state) across a retry elsewhere.
 */
export function lazyScreen<Screen extends ComponentType<never>>(
  load: () => Promise<Readonly<{ default: Screen }>>,
): Readonly<{ Component: Screen; reset: () => void }> {
  let failed = false;
  const create = () => lazy(() => load().then(
    (module) => ({ default: module.default as unknown as ComponentType<object> }),
    (error: unknown) => {
      failed = true;
      throw error;
    },
  ));
  let current = create();
  const LazyScreen = (props: object) => createElement(current, props);
  return {
    Component: LazyScreen as unknown as Screen,
    reset: () => {
      if (!failed) return;
      failed = false;
      current = create();
    },
  };
}

const failedLoadResets: (() => void)[] = [];
/** A loader inside a screen's chunk (Home's El Parte) joins the screens' retry. */
export const registerFailedLoadReset = (reset: () => void): void => { failedLoadResets.push(reset); };
/** A retry rebuilds only the loaders whose last load was rejected. */
export const resetFailedLoads = (): void => { for (const reset of failedLoadResets) reset(); };

interface ScreenLoadBoundaryProps extends PropsWithChildren {
  /** The screen on show: moving to another screen clears a failure. */
  readonly screen: string;
  /** Rebuilds the failed loaders before the screen is rendered again. */
  readonly onRetry: () => void;
  /** The one reload of the whole Panel (a module the browser cached as failed needs it). */
  readonly onReload?: () => void;
}

interface ScreenLoadBoundaryState {
  readonly screen: string;
  readonly failed: boolean;
  /** Vite could not preload a dependency: said above the screen, which stays as it is. */
  readonly preloadFailed: boolean;
  readonly generation: number;
}

const reloadPanel = () => window.location.reload();

/**
 * Contains a screen that cannot load or render. It sits below the session runtime (the session, the
 * shell and the navigation survive) and above the screen's own owners, so a failed chunk never
 * unmounts the Panel root: the person can retry the screen or reload the Panel.
 */
export class ScreenLoadBoundary extends Component<ScreenLoadBoundaryProps, ScreenLoadBoundaryState> {
  state: ScreenLoadBoundaryState = { screen: this.props.screen, failed: false, preloadFailed: false, generation: 0 };

  static getDerivedStateFromError(): Partial<ScreenLoadBoundaryState> {
    return { failed: true };
  }

  static getDerivedStateFromProps(props: ScreenLoadBoundaryProps, state: ScreenLoadBoundaryState): Partial<ScreenLoadBoundaryState> | null {
    return props.screen === state.screen ? null : { screen: props.screen, failed: false, preloadFailed: false };
  }

  /** A preload another owner handled (`preventDefault`, the door's night sheet) is not this screen's. */
  private readonly onPreloadError = (event: Event) => {
    if (!event.defaultPrevented) this.setState({ preloadFailed: true });
  };

  private readonly retry = () => {
    this.props.onRetry();
    this.setState((state) => ({ failed: false, preloadFailed: false, generation: state.generation + 1 }));
  };

  componentDidMount() {
    window.addEventListener('vite:preloadError', this.onPreloadError);
  }

  componentWillUnmount() {
    window.removeEventListener('vite:preloadError', this.onPreloadError);
  }

  private notice(retry: boolean): ReactNode {
    return <div className="runtime-failure" data-screen-load-error={this.props.screen}>
      <SystemBanner tone="critical" urgent>No se ha podido cargar la pantalla. Tu sesión sigue abierta.</SystemBanner>
      <div className="runtime-failure__actions">
        {retry ? <button className="btn btn--primary" onClick={this.retry} type="button">Reintentar</button> : null}
        <button className={retry ? 'btn btn--secondary' : 'btn btn--primary'} onClick={this.props.onReload ?? reloadPanel} type="button">Recargar el Panel</button>
      </div>
    </div>;
  }

  render(): ReactNode {
    if (this.state.failed) return this.notice(true);
    // A failed preload that did not stop the screen: the screen and its drafts stay; only a reload repairs it.
    return <>
      {this.state.preloadFailed ? this.notice(false) : null}
      <Fragment key={this.state.generation}>{this.props.children}</Fragment>
    </>;
  }
}
